import { dataTool } from "echarts/core";
import { DataAccess } from "../../../../dataaccess/data.access";
import { Cache, Invalidate } from "../../../../decorators/cache.decorator";
import { Timeout } from "../../../../decorators/timeout.decorator";
import { Budget } from "../Budget";

export class BudgetPlanService {
    @Cache({ targetName: "BudgetPlanService", immutable: true })
    public static getBudgetPlans(organizationId: number): Promise<Budget.BudgetPlan[]> {
        return DataAccess.get<Budget.BudgetPlan[]>(
            DataAccess.injectCaller(
                `/budget/plans.json?organizationId=${organizationId}`,
                "BudgetPlanService.getBudgetPlans",
            ),
        ).then((data) => data || []);
    }

    @Cache({ targetName: "BudgetPlanService", immutable: true })
    public static getBudgetPlan(planId: number): Promise<Budget.BudgetPlanResponse> {
        return DataAccess.get<Budget.BudgetPlanResponse>(
            DataAccess.injectCaller(`/budget/plan.json?budgetPlanId=${planId}`, "BudgetPlanService.getBudgetPlan"),
        );
    }

    @Invalidate({ serviceName: "BudgetPlanService" })
    public static createBudgetPlan(payload: Budget.BudgetPlan): Promise<Budget.BudgetPlanResponse> {
        payload = {
            ...payload,
            spentAmount: 0,
            committedAmount: 0,
            remainingAmount: 0,
            eventBudgets: [],
            lineItems: [],
            amount: 0,
            currency: "USD",
        };

        return DataAccess.post<Budget.BudgetPlanResponse>(
            DataAccess.injectCaller(`/budget/plan.json`, "BudgetPlanService.createBudgetPlan"),
            payload,
        );
    }

    @Invalidate({ serviceName: "BudgetPlanService" })
    public static updateBudgetPlan(
        budgetPlanId: number,
        payload: Partial<Budget.BudgetPlan>,
    ): Promise<Budget.BudgetPlanResponse> {
        return DataAccess.put<Budget.BudgetPlanResponse>(
            DataAccess.injectCaller(
                `/budget/plan.json?budgetPlanId=${budgetPlanId}`,
                "BudgetPlanService.updateBudgetPlan",
            ),
            payload,
        );
    }

    @Invalidate({ serviceName: "BudgetPlanService" })
    public static deleteBudgetPlan(budgetPlanId: number): Promise<{ success: boolean }> {
        return DataAccess.delete<{ success: boolean }>(
            DataAccess.injectCaller(
                `/budget/plan.json?budgetPlanId=${budgetPlanId}`,
                "BudgetPlanService.deleteBudgetPlan",
            ),
        );
    }

    /**
     * Submit a draft budget plan for approval by setting its status to PENDING.
     * The backend will initialize the workflow or auto-approve if no workflow is configured.
     */
    @Invalidate({ serviceName: "BudgetPlanService" })
    public static submitBudgetPlanForApproval(budgetPlanId: number): Promise<Budget.BudgetPlanResponse> {
        return this.updateBudgetPlan(budgetPlanId, { workflowStatus: "PENDING" });
    }
}
